// umlaut-check: skip — technische Schlüssel der JSON-Schnittstelle (laeuft, haengt, ueber) sind ASCII-Bezeichner.
import React, {useState} from 'react';
import OeTip from './OeTip';
import {fmt0} from './format';

// Automatisches Reservieren im Hintergrund (1.2.15): Stand je Position und
// Zusammenfassung. Der Server reserviert beim Speichern von Mengen von selbst
// (Lahr/Cosmos per Entnahme, Schneider über den früh angelegten Lieferschein
// „Neu"). Hier steht nur, was dabei herauskam — nichts davon passiert still.

const ERKLAERUNG = 'Sobald Mengen gespeichert werden, reserviert der Editor die Ware im Hintergrund für diesen Auftrag — '
    + 'ohne Klick. Lahr und Cosmos über eine Entnahme an der Position, Schneider (SNV) über den Lieferschein im '
    + 'Status „Neu", den weclapp beim Anlegen selbst reserviert. Der physische Bestand ändert sich dadurch nicht.';

/** Anzeige je Status: Beschriftung, Farbklasse, Erklärtext. */
const STATUS = {
    laeuft: {
        label: 'läuft …',
        cls: 'ar-run',
        tip: 'Die Reservierung für diese Position läuft gerade im Hintergrund. Die Anzeige aktualisiert sich von selbst — die Seite lädt nicht neu.',
    },
    reserved: {
        label: 'reserviert',
        cls: 'ar-ok',
        tip: 'Die offene Menge dieser Position ist für diesen Auftrag reserviert und steht anderen Aufträgen nicht mehr zur Verfügung.',
    },
    partial: {
        label: 'teilweise',
        cls: 'ar-part',
        tip: 'Nur ein Teil der offenen Menge ließ sich reservieren — mehr war nicht verfügbar. Bei Handelskunden zählt nur die ganze Verkaufseinheit; der Rest bleibt offen und kommt in die Fehlmengen-Datei.',
    },
    failed: {
        label: 'fehlgeschlagen',
        cls: 'ar-err',
        tip: 'Es ließ sich nichts reservieren. Der Grund steht in der Meldung — meist hält ein anderer Lieferschein oder Auftrag die Ware.',
    },
    blocked: {
        label: 'gesperrt',
        cls: 'ar-warn',
        tip: 'Hier wird bewusst nicht reserviert: Der Lahr-Bestand gilt noch als „in Pflege". Sobald die Einstellung „Lahr-Bestand belastbar" auf „Ja" steht, reserviert die Automatik auch in Lahr.',
    },
    aus: {
        label: 'aus',
        cls: 'ar-off',
        tip: 'Das automatische Reservieren ist für dieses Lager ausgeschaltet (Systemeinstellungen, Kategorie „Auftrags-Editor").',
    },
    dryrun: {
        label: 'Trockenlauf',
        cls: 'ar-off',
        tip: 'Der Schreibmodus ist nicht „live": Es wurde nichts in weclapp geschrieben. Die Meldung zeigt, was geschehen würde.',
    },
};

function zahl(n) {
    return fmt0(Number(n) || 0);
}

/** Die reservierenden Belege einer Meldung („Reserviert durch LS4497 40"). */
function HalterListe({halter, meldung}) {
    // Steht der erste Beleg schon im Text der Meldung, nicht doppelt nennen.
    if (!halter || !halter.length || (meldung && meldung.includes(halter[0].nummer))) {
        return null;
    }
    return (
        <span className="ar-halter">
            {' '}Reserviert durch:{' '}
            {halter.map((h, i) => (
                <span key={`${h.nummer}-${i}`}>{i ? ', ' : ''}{h.nummer} {zahl(h.menge)}</span>
            ))}
        </span>
    );
}

/**
 * Chip an einer Position der Tabelle. Ohne Ergebnis und ohne laufenden Lauf
 * erscheint nichts.
 *
 * @param {object} row - Zeile der Positionstabelle.
 * @param {object|null} position - Eintrag aus `ergebnis.positionen` oder null.
 * @param {boolean} laeuft - läuft gerade ein Lauf?
 */
export function AutoReserveChip({row, position, laeuft}) {
    if (!row || !row.id || row._new || !row.articleId || row.del) {
        return null;
    }

    const key = laeuft ? 'laeuft' : (position ? position.status : null);
    const s = key ? STATUS[key] : null;

    if (!s) {
        return null;
    }

    const menge = position && !laeuft && (position.status === 'partial')
        ? ` ${zahl(position.reserviert)}/${zahl(position.ziel)}`
        : '';

    const tip = (
        <span>
            <b>Automatisch reservieren: {s.label}</b>
            {position && !laeuft ? <><br/>{position.meldung}<HalterListe halter={position.halter} meldung={position.meldung}/></> : null}
            <br/>{s.tip}
        </span>
    );

    return (
        <OeTip tip={tip}>
            <span className={`archip ${s.cls}`} tabIndex={0} role="status" aria-label={`Automatisch reservieren: ${s.label}`}>
                {key === 'reserved' || key === 'partial' ? '🔒 ' : ''}{s.label}{menge}
            </span>
        </OeTip>
    );
}

/**
 * Statuskasten über der Positionstabelle. Verschwindet vollständig, solange es
 * weder einen Lauf noch ein Ergebnis zu zeigen gibt.
 *
 * @param {object} auto - Rückgabe von useAutoReserve.
 * @param {boolean} canWarehouse - darf einen Lauf anstoßen?
 */
const GESPERRT_KURZ = 12;

function GesperrtZeile({gruppe}) {
    const [alle, setAlle] = useState(false);
    const liste = alle ? gruppe.artikel : gruppe.artikel.slice(0, GESPERRT_KURZ);
    const rest = gruppe.artikel.length - liste.length;

    return (
        <div className="arzeile ar-blocked">
            <b>{gruppe.artikel.length === 1 ? '1 Position' : `${gruppe.artikel.length} Positionen`}</b>
            {' '}nicht reserviert — {String(gruppe.meldung || '').replace(/^Nicht reserviert:\s*/, '')}
            <div className="ar-artikel">
                {liste.join(', ')}
                {rest > 0 ? (
                    <>
                        {' '}
                        <OeTip tip="Zeigt alle Artikelnummern dieser Gruppe an. Es wird nichts gespeichert oder gesendet.">
                            <button type="button" className="link" onClick={() => setAlle(true)}>
                                + {rest} weitere
                            </button>
                        </OeTip>
                    </>
                ) : null}
            </div>
        </div>
    );
}

export default function AutoReservePanel({auto, canWarehouse, toast}) {
    const [fehler, setFehler] = useState('');
    const [busy, setBusy] = useState(false);
    const {stand, laeuft} = auto;

    if (!stand) {
        return null;
    }

    const ergebnis = stand.ergebnis;
    const haengt = !!stand.haengt;

    if (!ergebnis && !laeuft && !haengt) {
        return null;
    }

    const probleme = ((ergebnis && ergebnis.positionen) || []).filter(
        (p) => p.status === 'failed' || p.status === 'partial' || p.status === 'blocked',
    );
    // Gesperrte Positionen haben fast immer denselben Grund (Lahr in
    // Pflege) - eine Zeile je Grund statt einer Zeile je Position.
    const gesperrtGruppen = [];
    probleme.filter((p) => p.status === 'blocked').forEach((p) => {
        let g = gesperrtGruppen.find((x) => x.meldung === p.meldung);
        if (!g) {
            g = {meldung: p.meldung, artikel: []};
            gesperrtGruppen.push(g);
        }
        g.artikel.push(p.articleNumber);
    });
    const z = (ergebnis && ergebnis.zusammenfassung) || {};
    const hinweise = (ergebnis && ergebnis.hinweise) || [];
    const abgebrochen = ergebnis && ergebnis.ok === false && ergebnis.error;

    const anstossen = async () => {
        setBusy(true);
        setFehler('');
        const f = await auto.anstossen();
        setBusy(false);
        if (f) {
            setFehler(f);
            if (toast) {
                toast(`Reservierung nicht angestoßen: ${f}`, {variant: 'danger'});
            }
        }
    };

    const gut = ergebnis && !abgebrochen && !probleme.length && !ergebnis.uebersprungen;

    return (
        <div className={`arbar${probleme.length || abgebrochen || haengt ? ' ar-problem' : ''}`} role="region" aria-label="Automatisch reservieren">
            <div className="arhead">
                <OeTip tip={ERKLAERUNG}>
                    <b className="arkopf" tabIndex={0}>🔒 Automatisch reservieren</b>
                </OeTip>

                {laeuft ? (
                    <OeTip tip="Der Hintergrund-Job gleicht gerade die Reservierungen dieses Auftrags ab. Du kannst weiterarbeiten — die Anzeige aktualisiert sich von selbst.">
                        <span className="archip ar-run" tabIndex={0}>läuft …</span>
                    </OeTip>
                ) : null}

                {!laeuft && haengt ? (
                    <OeTip tip="Der Lauf hat sich seit über 15 Minuten nicht gemeldet. Vermutlich läuft der Queue-Arbeiter für die Queue „weclapp“ nicht — dann bleibt auch das Speichern im Hintergrund liegen.">
                        <span className="archip ar-err" tabIndex={0}>hängt — Arbeiter prüfen</span>
                    </OeTip>
                ) : null}

                {!laeuft && gut ? (
                    <OeTip tip="Alle Positionen dieses Auftrags, für die reserviert werden soll, sind versorgt.">
                        <span className="archip ar-ok" tabIndex={0}>✓ alles versorgt</span>
                    </OeTip>
                ) : null}

                {!laeuft && z.partial > 0 ? (
                    <OeTip tip="Positionen, bei denen nur ein Teil der offenen Menge reserviert werden konnte.">
                        <span className="archip ar-part" tabIndex={0}>{z.partial} teilweise</span>
                    </OeTip>
                ) : null}
                {!laeuft && z.failed > 0 ? (
                    <OeTip tip="Positionen, bei denen sich nichts reservieren ließ. Die Gründe stehen darunter.">
                        <span className="archip ar-err" tabIndex={0}>{z.failed} fehlgeschlagen</span>
                    </OeTip>
                ) : null}
                {!laeuft && z.blocked > 0 ? (
                    <OeTip tip="Positionen im Lager Lahr, solange der Lahr-Bestand als „in Pflege“ gilt.">
                        <span className="archip ar-warn" tabIndex={0}>{z.blocked} gesperrt (Lahr)</span>
                    </OeTip>
                ) : null}

                {ergebnis && ergebnis.trockenlauf ? (
                    <OeTip tip="Der Schreibmodus ist nicht „live“. Es wurde nichts in weclapp geschrieben.">
                        <span className="archip ar-off" tabIndex={0}>Trockenlauf</span>
                    </OeTip>
                ) : null}

                {canWarehouse ? (
                    <OeTip
                        tip="Gleicht die Reservierungen dieses Auftrags jetzt noch einmal ab; der Lagerbestand ändert sich nicht."
                        detail="Etwa nach einem Fehlschlag oder wenn in weclapp etwas freigeworden ist. Es wird nur reserviert, was verfügbar ist."
                    >
                        <button type="button" className="actbtn" disabled={busy || laeuft} onClick={anstossen}>
                            ↻ jetzt abgleichen
                        </button>
                    </OeTip>
                ) : null}
            </div>

            {abgebrochen ? (
                <div className="arzeile ar-fehler">
                    Das automatische Reservieren ist fehlgeschlagen: {ergebnis.error}
                </div>
            ) : null}

            {ergebnis && ergebnis.uebersprungen ? (
                <div className="arzeile">{ergebnis.uebersprungen}</div>
            ) : null}

            {fehler ? <div className="arzeile ar-fehler">{fehler}</div> : null}

            {!laeuft && gesperrtGruppen.map((g) => (
                <GesperrtZeile key={g.meldung} gruppe={g}/>
            ))}

            {!laeuft && probleme.filter((p) => p.status !== 'blocked').map((p) => (
                <div key={p.itemId} className={`arzeile ar-${p.status}`}>
                    <b>{p.articleNumber}</b>
                    {' '}· gebraucht {zahl(p.ziel)}, reserviert {zahl(p.reserviert)}
                    {' '}— {p.meldung}
                    <HalterListe halter={p.halter} meldung={p.meldung}/>
                </div>
            ))}

            {!laeuft && hinweise.map((h, i) => (
                <div key={i} className="arzeile ar-hinweis">{h}</div>
            ))}
        </div>
    );
}
